<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { consumeTokenFromFragment, fetchMe, loginRedirect, consumeReturnTo } from '../lib/auth.js';

  let { onAuthed } = $props();

  // Three terminal states for this page:
  //   working: fetchMe is in flight (initial mount or a "Try now" retry).
  //   notYetValid: token's nbf is in the future (clock skew). Token is kept;
  //     user clicks "Try now" to retry without losing it.
  //   error: token was wiped (expired / bad signature / no token in URL).
  let working = $state(true);
  let notYetValid = $state(false);
  let error = $state('');
  let retrying = $state(false);

  async function validate() {
    const r = await fetchMe();
    if (r.user) {
      onAuthed?.(r.user);
      // Drop the user back where they were before they were bounced to
      // login. Falls through to /captures when there's no saved URL
      // (fresh login from the Welcome page, etc.).
      navigate(consumeReturnTo(), { replace: true });
      return;
    }
    if (r.code === 'not_yet_valid') {
      notYetValid = true;
      error = '';
    } else {
      notYetValid = false;
      error = r.message || 'Token rejected by server.';
    }
  }

  async function retry() {
    retrying = true;
    try { await validate(); } finally { retrying = false; }
  }

  onMount(async () => {
    const token = consumeTokenFromFragment();
    if (!token) {
      working = false;
      error = 'No token in callback URL.';
      return;
    }
    try { await validate(); } finally { working = false; }
  });
</script>

<div class="flex items-center justify-center h-screen text-on-surface-variant">
  {#if working}
    <p>Signing you in...</p>
  {:else if notYetValid}
    <div class="text-center max-w-md px-6">
      <span class="material-symbols-outlined text-amber-500 text-5xl mb-2">schedule</span>
      <p class="font-medium mb-1">JWT is not valid yet</p>
      <p class="text-sm text-on-surface-variant mb-4">
        Your token's start time hasn't elapsed yet — usually a few seconds of
        clock skew between your machine and the auth server. The token has
        been kept; click below to retry validation.
      </p>
      <button
        onclick={retry}
        disabled={retrying}
        class="px-5 py-2 rounded-full bg-primary text-on-primary disabled:opacity-50"
      >
        {retrying ? 'Trying…' : 'Try now'}
      </button>
    </div>
  {:else if error}
    <div class="text-center">
      <p class="text-error mb-4">{error}</p>
      <button onclick={loginRedirect} class="px-5 py-2 rounded-full bg-primary text-on-primary">
        Sign in again
      </button>
    </div>
  {/if}
</div>
